// Payment.jsx - Data Analitika = AI Kodu Mapping
// Versiya 4.5 - Data Analitika kursu AI kodunu çəkir

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Upload, 
  CreditCard, 
  CheckCircle, 
  AlertCircle, 
  Loader2, 
  Copy, 
  ChevronRight, 
  Shield, 
  Zap,
  X,
  Check,
  ArrowLeft,
  Lock,
  FileText,
  AlertTriangle,
  Clock,
  Calendar,
  Hash,
  Eye,
  Info
} from 'lucide-react';
import { auth, db } from '../firebase';
import { 
  doc, 
  getDoc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  getDocs, 
  serverTimestamp, 
  addDoc,
  setDoc
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { createWorker } from 'tesseract.js';
import '../styles/Payment.css';

// =============================================================================
// KONFIGURASIYA
// =============================================================================

const COURSES = {
  frontend: { 
    name: 'Frontend Development', 
    price: 17, 
    dbName: 'frontend',
    icon: '💻',
    color: '#6366f1',
    description: 'HTML, CSS, JavaScript, React'
  },
  data: { 
    name: 'Data Analitika', 
    price: 23, 
    dbName: 'ai-python', // Firestore-da AI kursunun adı ilə axtarır
    icon: '📊',
    color: '#ec4899',
    description: 'Python, SQL, Data Visualization'
  }
};

// BÜTÜN MƏBLƏĞLƏR
const VALID_AMOUNTS = [23, 17, 20.7, 15.3, 18.4, 13.6, 0]; // 0 = PULSUZ

// 4 Fərqli Kart
const PAYMENT_CARDS = [
  { 
    id: 'birbank_4169', 
    bank: 'Birbank', 
    number: '4169 7388 2323 0671', 
    last4: '0671',
    color: '#F59E0B',
    logo: 'B',
    bg: 'linear-gradient(135deg, #F59E0B, #D97706)'
  },
  { 
    id: 'leobank_5411', 
    bank: 'Leobank', 
    number: '5411 2498 0791 2345', 
    last4: '2345',
    color: '#10B981',
    logo: 'L',
    bg: 'linear-gradient(135deg, #10B981, #059669)'
  },
  { 
    id: 'abb_5522', 
    bank: 'ABB', 
    number: '5522 0993 8163 8170', 
    last4: '8170',
    color: '#3B82F6',
    logo: 'A',
    bg: 'linear-gradient(135deg, #3B82F6, #2563EB)'
  },
  { 
    id: 'unibank_5243', 
    bank: 'Unibank', 
    number: '5243 7544 4983 5550', 
    last4: '5550',
    color: '#8B5CF6',
    logo: 'U',
    bg: 'linear-gradient(135deg, #8B5CF6, #7C3AED)'
  }
];

// Tarix toleransı: 24 saat
const TIME_TOLERANCE_MS = 24 * 60 * 60 * 1000;

// =============================================================================
// KÖMƏKÇİ FUNKSIYALAR
// =============================================================================

const getRandomCard = () => {
  const timestamp = Date.now();
  const randomSeed = Math.sin(timestamp) * 10000;
  const randomIndex = Math.floor(Math.abs(randomSeed) % PAYMENT_CARDS.length);
  return PAYMENT_CARDS[randomIndex];
};

// Advanced Image Preprocessing
const preprocessImage = async (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      let width = img.width;
      let height = img.height;
      
      const maxDimension = 2000;
      if (width > maxDimension || height > maxDimension) {
        const ratio = Math.min(maxDimension / width, maxDimension / height);
        width *= ratio;
        height *= ratio;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, width, height);
      
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      for (let i = 0; i < data.length; i += 4) {
        const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        const contrast = 1.1;
        const adjusted = ((gray - 128) * contrast) + 128;
        const final = Math.max(0, Math.min(255, adjusted));
        
        data[i] = final;
        data[i + 1] = final;
        data[i + 2] = final;
      }
      
      ctx.putImageData(imageData, 0, 0);
      
      const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
      resolve(dataUrl);
    };
    
    img.onerror = () => reject(new Error('Şəkil yüklənə bilmədi'));
    img.src = URL.createObjectURL(file);
  });
};

// Tarix parse funksiyası
const parseDateFromText = (text) => {
  const patterns = [
    { regex: /(\d{1,2})[.\-\/](\d{1,2})[.\-\/](20\d{2})/, type: 'DMY' },
    { regex: /(20\d{2})[.\-\/](\d{1,2})[.\-\/](\d{1,2})/, type: 'YMD' },
    { regex: /(\d{1,2})[.\-\/](\d{1,2})[.\-\/](\d{2})/, type: 'DMY_SHORT' },
    { 
      regex: /(\d{1,2})\s+(yanvar|fevral|mart|aprel|may|iyun|iyul|avqust|sentyabr|oktyabr|noyabr|dekabr)[a-z]*\s+(20\d{2})/i, 
      type: 'TEXT' 
    }
  ];

  const months = {
    'yanvar': 0, 'fevral': 1, 'mart': 2, 'aprel': 3, 'may': 4, 'iyun': 5,
    'iyul': 6, 'avqust': 7, 'sentyabr': 8, 'oktyabr': 9, 'noyabr': 10, 'dekabr': 11
  };

  for (const pattern of patterns) {
    const match = text.match(pattern.regex);
    if (match) {
      try {
        let day, month, year;
        
        if (pattern.type === 'DMY' || pattern.type === 'DMY_SHORT') {
          day = parseInt(match[1]);
          month = parseInt(match[2]) - 1;
          year = parseInt(match[3]);
          if (year < 100) year += 2000;
        } else if (pattern.type === 'YMD') {
          year = parseInt(match[1]);
          month = parseInt(match[2]) - 1;
          day = parseInt(match[3]);
        } else if (pattern.type === 'TEXT') {
          day = parseInt(match[1]);
          month = months[match[2].toLowerCase()];
          year = parseInt(match[3]);
        }

        const date = new Date(year, month, day);
        if (date.getFullYear() === year && date.getMonth() === month && date.getDate() === day) {
          return date;
        }
      } catch (e) {
        continue;
      }
    }
  }
  return null;
};

// Saat parse funksiyası
const parseTimeFromText = (text) => {
  const patterns = [
    /(\d{1,2})[:.](\d{2})(?::(\d{2}))?/,
    /(\d{1,2})[:.](\d{2})\s*(AM|PM)/i,
    /(\d{2})[.:](\d{2})/
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      let hours = parseInt(match[1]);
      const minutes = parseInt(match[2]);
      const seconds = match[3] ? parseInt(match[3]) : 0;
      const ampm = match[4]?.toUpperCase();
      
      if (ampm === 'PM' && hours < 12) hours += 12;
      if (ampm === 'AM' && hours === 12) hours = 0;
      
      if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
        return { hours, minutes, seconds };
      }
    }
  }
  return null;
};

const parseFullDateTime = (text) => {
  const date = parseDateFromText(text);
  const time = parseTimeFromText(text);
  
  if (date && time) {
    date.setHours(time.hours, time.minutes, time.seconds);
    return date;
  }
  
  if (date) {
    date.setHours(12, 0, 0, 0);
    return date;
  }
  
  return null;
};

// =============================================================================
// MƏBLƏĞ EXTRACT FUNKSİYASI
// =============================================================================

const extractAndValidateAmount = (text, expectedAmount) => {
  const lowerText = text.toLowerCase();
  const normalizedExpected = Math.round(parseFloat(expectedAmount) * 100) / 100;
  
  if (normalizedExpected === 0) {
    return {
      valid: true,
      found: 0,
      expected: 0,
      normalized: 0,
      hasCurrency: true,
      raw: 'PULSUZ',
      diff: 0,
      isFree: true
    };
  }
  
  const hasCurrency = /(?:azn|manat|₼|m\.|currency)/i.test(text);
  
  const amountPatterns = [
    /(\d{1,3}[.,]\d{1,2})\s*(?:azn|manat|₼|m\.?)/i,
    /(?:azn|manat|₼)\s*:?\s*(\d{1,3}[.,]\d{1,2})/i,
    /m[əe]bl[əe][gğ][:\s]*(\d{1,3}[.,]\d{1,2})/i,
    /amount[:\s]*(\d{1,3}[.,]\d{1,2})/i,
    /(?:total|ümumi|yekun)[:\s]*(\d{1,3}[.,]\d{1,2})/i,
    /(?:sum|cem|сум)[:\s]*(\d{1,3}[.,]\d{1,2})/i,
    /(\d{1,3}[.,]\d{2})(?=\s*(?:azn|manat|₼|$))/i,
    /\b(\d{2}[.,]\d{1,2})\b/g
  ];
  
  let foundAmounts = [];
  
  for (const pattern of amountPatterns) {
    if (pattern.global) {
      const matches = text.match(pattern);
      if (matches) {
        matches.forEach(match => {
          const cleanValue = match.replace(',', '.');
          const parsed = parseFloat(cleanValue);
          if (!isNaN(parsed) && parsed > 10 && parsed < 100) {
            foundAmounts.push({ value: parsed, raw: match });
          }
        });
      }
    } else {
      const match = text.match(pattern);
      if (match) {
        const rawValue = match[1] || match[0];
        const cleanValue = rawValue.replace(/[^\d.,]/g, '').replace(',', '.');
        const parsed = parseFloat(cleanValue);
        if (!isNaN(parsed) && parsed > 0) {
          foundAmounts.push({ value: parsed, raw: rawValue });
        }
      }
    }
  }
  
  foundAmounts = foundAmounts.filter((item, index, self) => 
    index === self.findIndex(t => Math.abs(t.value - item.value) < 0.001)
  );
  
  const TOLERANCE = 0.05;
  
  for (const amount of foundAmounts) {
    const normalizedFound = Math.round(amount.value * 100) / 100;
    const diff = Math.abs(normalizedFound - normalizedExpected);
    
    if (diff < TOLERANCE) {
      return {
        valid: true,
        found: amount.value,
        expected: expectedAmount,
        normalized: normalizedExpected,
        hasCurrency: hasCurrency,
        raw: amount.raw,
        diff: diff
      };
    }
  }
  
  let closest = null;
  let minDiff = Infinity;
  
  for (const amount of foundAmounts) {
    const diff = Math.abs(amount.value - normalizedExpected);
    if (diff < minDiff) {
      minDiff = diff;
      closest = amount;
    }
  }
  
  return {
    valid: false,
    found: closest?.value || null,
    expected: expectedAmount,
    normalized: normalizedExpected,
    hasCurrency: hasCurrency,
    allFound: foundAmounts.map(a => a.value),
    closestDiff: minDiff !== Infinity ? minDiff : null
  };
};

// =============================================================================
// ƏSAS KOMPONENT
// =============================================================================

const Payment = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const workerRef = useRef(null);
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [email, setEmail] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [finalPrice, setFinalPrice] = useState(0);
  const [basePrice, setBasePrice] = useState(0);
  const [discountApplied, setDiscountApplied] = useState(false);
  const [firebaseUserId, setFirebaseUserId] = useState(null);
  const [affiliateName, setAffiliateName] = useState('');
  const [isFreeCourse, setIsFreeCourse] = useState(false);
  
  const [assignedCard, setAssignedCard] = useState(null);
  
  const [uploadedFile, setUploadedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [validationDetails, setValidationDetails] = useState(null);
  const [extractedText, setExtractedText] = useState('');
  
  const [activationCode, setActivationCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copied, setCopied] = useState(false);
  const [showFraudWarning, setShowFraudWarning] = useState(false);
  const [showDebugInfo, setShowDebugInfo] = useState(false);

  const [currentTime, setCurrentTime] = useState(new Date());

  // Tesseract Worker initialization
  useEffect(() => {
    const initWorker = async () => {
      try {
        workerRef.current = await createWorker('aze+eng', 1, {
          logger: (m) => {
            if (m.status === 'recognizing text') {
              setOcrProgress(Math.round(m.progress * 100));
            }
          }
        });
      } catch (err) {
        console.error('Worker initialization error:', err);
      }
    };
    
    initWorker();
    
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
      }
    };
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        setEmail(currentUser.email || '');
      }
      setLoading(false);
    });
    
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    
    return () => {
      unsubscribe();
      clearInterval(timeInterval);
    };
  }, []);

  const handleCourseSelect = (courseKey) => {
    const price = COURSES[courseKey].price;
    setSelectedCourse(courseKey);
    setBasePrice(price);
    setFinalPrice(price);
    
    const randomCard = getRandomCard();
    setAssignedCard(randomCard);
    
    setCurrentStep(2);
    setError('');
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsProcessing(true);

    try {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setError('Düzgün email formatı daxil edin');
        setIsProcessing(false);
        return;
      }

      const botDataRef = collection(db, 'botActivationData');
      const q = query(botDataRef, where('userEmail', '==', email.toLowerCase().trim()));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError('Bu email sistemdə tapılmadı! Zəhmət olmasa əvvəlcə qeydiyyatdan keçin.');
        setIsProcessing(false);
        return;
      }

      const userDoc = querySnapshot.docs[0];
      const userData = userDoc.data();
      
      // Data Analitika kursu seçilibsə, AI kursunun kodunu yoxla
      const courseDbName = COURSES[selectedCourse].dbName;
      const courseInfo = userData.courses?.[courseDbName];
      
      if (courseInfo?.status === 'active') {
        setError(`Bu kurs üçün artıq aktivləşdirmə kodu mövcuddur.`);
        setIsProcessing(false);
        return;
      }

      setFirebaseUserId(userDoc.id);
      setCurrentStep(3);
    } catch (err) {
      setError('Xəta baş verdi: ' + err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePromoSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsProcessing(true);

    try {
      const inputCode = promoCode.trim().toUpperCase();
      
      // PULSUZ KODLAR (100% endirim)
      if (inputCode === 'LUMINA100' || inputCode === 'REYZERS100') {
        setFinalPrice(0);
        setDiscountApplied(true);
        setIsFreeCourse(true);
        setAffiliateName(inputCode === 'LUMINA100' ? 'LUMINA100 - PULSUZ' : 'REYZERS100 - PULSUZ');
        setCurrentStep(4);
        setIsProcessing(false);
        return;
      }
      
      if (!inputCode || inputCode === 'XEYR' || inputCode === 'YOXDUR') {
        setDiscountApplied(false);
        setFinalPrice(basePrice);
        setIsFreeCourse(false);
        setCurrentStep(4);
        setIsProcessing(false);
        return;
      }

      const validPromoCodes = ['LUMINA20', 'AI2024', 'FRONT17'];
      
      if (validPromoCodes.includes(inputCode)) {
        const discountedPrice = Math.round(basePrice * 0.8 * 100) / 100;
        setFinalPrice(discountedPrice);
        setDiscountApplied(true);
        setIsFreeCourse(false);
        setAffiliateName('Promo Tərəfdaş');
        setCurrentStep(4);
      } else {
        setError('Promo kod tapılmadı! Düzgün kod daxil edin.');
        setIsProcessing(false);
        return;
      }
      
    } catch (err) {
      setError('Xəta baş verdi: ' + err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const skipPromo = () => {
    setDiscountApplied(false);
    setFinalPrice(basePrice);
    setIsFreeCourse(false);
    setPromoCode('');
    setAffiliateName('');
    setCurrentStep(4);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) validateAndSetFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) validateAndSetFile(file);
  };

  const validateAndSetFile = (file) => {
    setError('');
    
    if (!file.type.startsWith('image/')) {
      setError('Zəhmət olmasa şəkil faylı yükləyin (JPG, PNG, WEBP)');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('Fayl həcmi 10MB-dan çox ola bilməz');
      return;
    }

    setUploadedFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => setFilePreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const removeFile = () => {
    setUploadedFile(null);
    setFilePreview(null);
    setExtractedText('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Validasiya funksiyası (admin üçün gizli saxlanılır)
  const validatePaymentReceipt = useCallback((text, expectedAmount, cardLast4, currentDateTime) => {
    const lowerText = text.toLowerCase();
    
    let score = 0;
    let maxScore = 0;
    const details = {
      checks: [],
      amounts: [],
      dates: [],
      times: [],
      banks: [],
      statuses: [],
      cardLast4: null,
      confidence: 0
    };

    // PULSUZ KURS YOXLANIŞI
    if (expectedAmount === 0) {
      return {
        isValid: true,
        percentage: 100,
        details: {
          checks: ['✅ PULSUZ aktivləşdirmə'],
          amounts: [],
          dates: [],
          times: [],
          banks: [],
          statuses: [],
          cardLast4: null,
          confidence: 100
        },
        score: 100,
        maxScore: 100,
        amountCheck: { valid: true, isFree: true },
        criticalFields: true
      };
    }

    // 1. MƏBLƏĞ YOXLANIŞI (35 bal)
    maxScore += 35;
    const amountValidation = extractAndValidateAmount(text, expectedAmount);
    
    if (amountValidation.valid) {
      score += 35;
      details.amounts.push({ 
        value: amountValidation.found, 
        raw: amountValidation.raw,
        diff: amountValidation.diff
      });
      details.checks.push(`✅ Məbləğ doğrudur: ${amountValidation.found} AZN`);
    } else {
      details.checks.push(`❌ Məbləğ uyğun deyil`);
      if (amountValidation.closestDiff && amountValidation.closestDiff < 1) {
        score += 20;
      }
    }

    // 2. TARIX YOXLANIŞI (20 bal)
    maxScore += 20;
    let dateValid = false;
    const receiptDate = parseFullDateTime(text);
    
    if (receiptDate) {
      details.dates.push(receiptDate.toISOString());
      const timeDiff = currentDateTime.getTime() - receiptDate.getTime();
      
      if (timeDiff < 0) {
        score += 10;
      } else if (timeDiff > TIME_TOLERANCE_MS) {
        score += 10;
      } else {
        score += 20;
        dateValid = true;
        details.checks.push(`✅ Tarix uyğundur`);
      }
    } else {
      score += 5;
    }

    // 3. KART SON 4 RƏQƏM (15 bal)
    maxScore += 15;
    let cardFound = false;
    
    const cardPatterns = [
      new RegExp(`\\*{1,4}\\s*${cardLast4}`),
      new RegExp(`\\*\\s*${cardLast4}\\b`),
      new RegExp(`\\b${cardLast4}\\b`),
      new RegExp(`(\\d{4}\\s*){3}${cardLast4}`),
      new RegExp(`\\d{4}\\s*\\*\\s*${cardLast4}`),
      new RegExp(`${cardLast4}\\s*\\*`),
      new RegExp(`xx${cardLast4}`, 'i'),
      new RegExp(`\\*+${cardLast4}`)
    ];

    for (const pattern of cardPatterns) {
      if (pattern.test(text) || pattern.test(lowerText)) {
        score += 15;
        cardFound = true;
        details.cardLast4 = cardLast4;
        details.checks.push(`✅ Kart son 4 rəqəm tapıldı`);
        break;
      }
    }

    // 4. VALYUTA VƏ ƏMƏLIYYAT (10 bal)
    maxScore += 10;
    const currencyPatterns = [/azn/i, /₼/, /manat/i, /məblə[ğg]/i, /mebleg/i];
    
    for (const pattern of currencyPatterns) {
      if (pattern.test(lowerText)) {
        score += 5;
        details.checks.push(`✅ Valyuta tapıldı`);
        break;
      }
    }
    
    const operationPatterns = [/köçürmə/i, /transfer/i, /ödəniş/i, /payment/i, /əməliyyat/i];
    for (const pattern of operationPatterns) {
      if (pattern.test(lowerText)) {
        score += 5;
        details.checks.push(`✅ Əməliyyat növü tapıldı`);
        break;
      }
    }

    // 5. UĞUR STATUSU (10 bal)
    maxScore += 10;
    const successPatterns = [
      /u[ğg]urlu/i, /success/i, /completed/i, /approved/i,
      /təsdiqləndi/i, /tesdiqlendi/i, /yerinə\s*yetirildi/i, /ödəniş\s*edildi/i,
      /odenis\s*edildi/i, /✓/, /✅/, /✔/, /🟢/
    ];

    for (const pattern of successPatterns) {
      if (pattern.test(text) || pattern.test(lowerText)) {
        score += 10;
        details.statuses.push('success');
        details.checks.push(`✅ Uğurlu status tapıldı`);
        break;
      }
    }

    // 6. BANK ADI (10 bal)
    maxScore += 10;
    const bankPatterns = [
      { name: 'Birbank', pattern: /birbank/i },
      { name: 'Leobank', pattern: /leo|leobank/i },
      { name: 'Unibank', pattern: /unibank/i },
      { name: 'ABB', pattern: /abb/i },
      { name: 'Kapital Bank', pattern: /kapital/i },
      { name: 'PASHA Bank', pattern: /pasha/i },
      { name: 'Xalq Bank', pattern: /xalq/i }
    ];

    for (const bank of bankPatterns) {
      if (bank.pattern.test(lowerText)) {
        score += 10;
        details.banks.push(bank.name);
        details.checks.push(`✅ Bank tapıldı: ${bank.name}`);
        break;
      }
    }

    const percentage = Math.round((score / maxScore) * 100);
    details.confidence = percentage;
    details.score = score;
    details.maxScore = maxScore;
    
    const amountScore = amountValidation.valid ? 35 : (amountValidation.closestDiff < 1 ? 20 : 0);
    const hasCriticalFields = amountScore >= 20 && (dateValid || details.banks.length > 0);
    
    const isValid = (percentage >= 60 && amountScore >= 20) || 
                    (hasCriticalFields && percentage >= 50);

    return {
      isValid: isValid,
      percentage: percentage,
      details: details,
      score: score,
      maxScore: maxScore,
      amountCheck: amountValidation,
      criticalFields: hasCriticalFields
    };
  }, []);

  const processPayment = async () => {
    if (!uploadedFile && !isFreeCourse) {
      setError('Zəhmət olmasa ödəniş çekinin şəklini yükləyin');
      return;
    }

    if (!assignedCard && !isFreeCourse) {
      setError('Sistem xətası: Kart məlumatları tapılmadı');
      return;
    }

    if (!workerRef.current && !isFreeCourse) {
      setError('OCR sistemi hazır deyil. Zəhmət olmasa səhifəni yeniləyin.');
      return;
    }

    // PULSUZ KURS
    if (isFreeCourse) {
      setIsProcessing(true);
      try {
        const now = new Date();
        const freeValidation = {
          isValid: true,
          percentage: 100,
          details: {
            checks: ['✅ PULSUZ aktivləşdirmə'],
            amounts: [],
            dates: [],
            times: [],
            banks: [],
            statuses: [],
            cardLast4: null,
            confidence: 100
          },
          score: 100,
          maxScore: 100,
          amountCheck: { valid: true, isFree: true },
          criticalFields: true
        };
        
        await activateCode('PULSUZ', freeValidation, now);
        return;
      } catch (err) {
        setError('Xəta baş verdi: ' + err.message);
        setIsProcessing(false);
        return;
      }
    }

    setIsProcessing(true);
    setError('');
    setValidationDetails(null);

    try {
      const now = new Date();
      
      let processedImage;
      try {
        processedImage = await preprocessImage(uploadedFile);
      } catch (preprocessErr) {
        processedImage = filePreview;
      }
      
      const result = await workerRef.current.recognize(processedImage);
      const text = result.data.text;
      setExtractedText(text);
      
      const validation = validatePaymentReceipt(
        text, 
        finalPrice, 
        assignedCard.last4,
        now
      );
      
      setValidationDetails(validation);
      setOcrProgress(100);
      
      if (!validation.isValid && !showDebugInfo) {
        let errorMsg = `❌ Çek təsdiqlənmədi. Zəhmət olmasa şəklin aydın olduğundan əmin olun.`;
        setError(errorMsg);
        setIsProcessing(false);
        return;
      }

      await activateCode(text, validation, now);

    } catch (err) {
      setError('Şəkil oxuma xətası: ' + err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const activateCode = async (ocrText, validation, paymentTime) => {
    try {
      // Data Analitika kursu üçün AI kursunun dbName-ni istifadə et
      const courseDbName = COURSES[selectedCourse].dbName;
      
      const botRef = doc(db, 'botActivationData', firebaseUserId);
      const botSnap = await getDoc(botRef);

      if (!botSnap.exists()) {
        setError('İstifadəçi məlumatları tapılmadı');
        setIsProcessing(false);
        return;
      }

      const botData = botSnap.data();
      const courseData = botData.courses?.[courseDbName];
      
      if (!courseData?.code) {
        setError('Aktivləşdirmə kodu tapılmadı');
        setIsProcessing(false);
        return;
      }

      const code = courseData.code;

      // Firestore güncəlləmələri - Data Analitika = AI kursu
      await updateDoc(botRef, {
        [`courses.${courseDbName}.status`]: 'active',
        [`courses.${courseDbName}.payment.status`]: 'completed',
        [`courses.${courseDbName}.payment.verifiedAt`]: serverTimestamp(),
        [`courses.${courseDbName}.payment.amount`]: finalPrice,
        [`courses.${courseDbName}.payment.validationScore`]: validation.score,
        [`courses.${courseDbName}.payment.validationPercentage`]: validation.percentage,
        [`courses.${courseDbName}.payment.cardUsed`]: isFreeCourse ? promoCode.toUpperCase() : assignedCard.id,
        [`courses.${courseDbName}.payment.receiptTime`]: paymentTime.toISOString(),
        updatedAt: serverTimestamp()
      });

      await addDoc(collection(db, 'payments'), {
        email: email.toLowerCase(),
        course: selectedCourse,
        courseName: COURSES[selectedCourse].name,
        finalPrice: finalPrice,
        basePrice: basePrice,
        discountApplied: discountApplied,
        discountPercent: isFreeCourse ? 100 : (discountApplied ? 20 : 0),
        affiliateCode: promoCode.toUpperCase() || null,
        affiliateName: affiliateName || null,
        firebaseUserId: firebaseUserId,
        date: serverTimestamp(),
        status: 'completed',
        ocrText: ocrText.substring(0, 1000),
        validationScore: validation.score,
        validationPercentage: validation.percentage,
        validationDetails: validation.details,
        cardUsed: isFreeCourse ? promoCode.toUpperCase() : assignedCard.id,
        cardLast4: isFreeCourse ? 'FREE' : assignedCard.last4,
        cardBank: isFreeCourse ? 'PROMO' : assignedCard.bank,
        receiptTimestamp: paymentTime.toISOString(),
        verifiedBy: isFreeCourse ? 'FREE_PROMO' : 'ocr_system',
        // Qeyd: Data Analitika ödənişi AI kursunun kodunu aktivləşdirir
        actualCourseMapped: courseDbName
      });

      const yesPaymentRef = doc(collection(db, 'yesPayments'));
      await setDoc(yesPaymentRef, {
        email: email.toLowerCase(),
        course: selectedCourse,
        courseName: COURSES[selectedCourse].name,
        amount: finalPrice,
        currency: 'AZN',
        paymentDate: serverTimestamp(),
        receiptTimestamp: paymentTime.toISOString(),
        cardUsed: isFreeCourse ? promoCode.toUpperCase() : assignedCard.id,
        cardBank: isFreeCourse ? 'PROMO' : assignedCard.bank,
        cardLast4: isFreeCourse ? 'FREE' : assignedCard.last4,
        firebaseUserId: firebaseUserId,
        promoCode: promoCode.toUpperCase() || null,
        affiliateName: affiliateName || null,
        activationCode: code,
        validationScore: validation.score,
        validationPercentage: validation.percentage,
        status: 'completed',
        createdAt: serverTimestamp(),
        // Qeyd: Data Analitika ödənişi AI kursunun kodunu aktivləşdirir
        actualCourseMapped: courseDbName
      });

      // WhatsApp və digər admin funksiyaları backend-də həyata keçirilir
      // Frontend-də göstərilmir

      setActivationCode(code);
      setShowFraudWarning(true);
      setCurrentStep(5);
      setSuccess(`✅ Ödəniş uğurla təsdiqləndi!`);
      
    } catch (err) {
      setError('Aktivləşdirmə xətası: ' + err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(activationCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const resetForm = () => {
    setCurrentStep(1);
    setSelectedCourse(null);
    setEmail('');
    setPromoCode('');
    setFinalPrice(0);
    setBasePrice(0);
    setDiscountApplied(false);
    setIsFreeCourse(false);
    setAssignedCard(null);
    setUploadedFile(null);
    setFilePreview(null);
    setActivationCode('');
    setError('');
    setSuccess('');
    setCopied(false);
    setAffiliateName('');
    setValidationDetails(null);
    setShowFraudWarning(false);
    setOcrProgress(0);
    setExtractedText('');
    setShowDebugInfo(false);
  };

  if (loading) {
    return (
      <div className="payment-page-loading">
        <Loader2 className="animate-spin" size={48} color="#6366f1" />
        <p>Yüklənir...</p>
      </div>
    );
  }

  return (
    <div className="payment-page">
      <header className="payment-header-simple">
        <Link to="/" className="payment-logo">
          <Zap size={28} color="#6366f1" />
          <span>Lumina Ödəniş</span>
        </Link>
        <div className="payment-header-actions">
          {user ? (
            <span className="user-email">{user.email}</span>
          ) : (
            <Link to="/login" className="btn-login-small">Giriş</Link>
          )}
        </div>
      </header>

      <main className="payment-content">
        <div className="payment-progress">
          {[
            { num: 1, label: 'Kurs' },
            { num: 2, label: 'Email' },
            { num: 3, label: 'Promo' },
            { num: 4, label: isFreeCourse ? 'Təsdiq' : 'Çek' },
            { num: 5, label: 'Kod' }
          ].map((step) => (
            <div 
              key={step.num}
              className={`progress-item ${currentStep >= step.num ? 'active' : ''} ${currentStep === step.num ? 'current' : ''}`}
            >
              <div className="progress-circle">{step.num}</div>
              <span className="progress-label">{step.label}</span>
            </div>
          ))}
        </div>

        {error && (
          <div className="payment-alert error">
            <AlertCircle size={20} />
            <div className="alert-content">
              {error.split('\n').map((line, i) => (
                <p key={i} style={{ margin: '0.25rem 0', whiteSpace: 'pre-wrap' }}>{line}</p>
              ))}
            </div>
            <button onClick={() => setError('')} className="alert-close"><X size={16} /></button>
          </div>
        )}

        {success && (
          <div className="payment-alert success">
            <CheckCircle size={20} />
            <span>{success}</span>
          </div>
        )}

        {currentStep === 1 && (
          <div className="payment-step">
            <h1>Kurs Seçin</h1>
            <p className="step-description">Hansı kurs üçün ödəniş etmək istəyirsiniz?</p>
            
            <div className="course-selection-grid">
              {Object.entries(COURSES).map(([key, course]) => (
                <div 
                  key={key}
                  className="course-selection-card"
                  onClick={() => handleCourseSelect(key)}
                  style={{ borderColor: course.color }}
                >
                  <div className="course-selection-icon" style={{ background: course.color }}>
                    {course.icon}
                  </div>
                  <div className="course-selection-info">
                    <h3>{course.name}</h3>
                    <p>{course.description}</p>
                    <div className="course-selection-price" style={{ color: course.color }}>
                      {course.price} AZN/ay
                    </div>
                  </div>
                  <ChevronRight size={24} className="selection-arrow" />
                </div>
              ))}
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="payment-step">
            <h1>Email Təsdiqi</h1>
            <p className="step-description">Qeydiyyatdan keçdiyiniz email ünvanı</p>
            
            <form onSubmit={handleEmailSubmit} className="payment-form">
              <div className="form-field">
                <label>Email ünvanı</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ad@example.com"
                  required
                  disabled={!!user}
                />
              </div>

              <div className="selected-course-preview">
                <span>Seçilmiş kurs:</span>
                <strong>{COURSES[selectedCourse]?.name}</strong>
                <span>{COURSES[selectedCourse]?.price} AZN</span>
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => setCurrentStep(1)} className="btn-secondary">
                  <ArrowLeft size={18} /> Geri
                </button>
                <button type="submit" className="btn-primary" disabled={isProcessing}>
                  {isProcessing ? <Loader2 className="animate-spin" size={18} /> : <ChevronRight size={18} />}
                  Davam et
                </button>
              </div>
            </form>
          </div>
        )}

        {currentStep === 3 && (
          <div className="payment-step">
            <h1>Promo Kod</h1>
            <p className="step-description">Endirim kodunuz varsa daxil edin</p>
            
            <form onSubmit={handlePromoSubmit} className="payment-form">
              <div className="form-field">
                <label>Promo Kod (opsional)</label>
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="Kodu daxil edin və ya 'Xeyr' yazın"
                  style={{ textTransform: 'uppercase' }}
                />
                {affiliateName && (
                  <span className="field-hint success">✅ {affiliateName}</span>
                )}
              </div>

              {/* Promo kodlar siyahısı silindi - admin üçün gizli */}

              <div className="price-breakdown">
                <div className="price-row">
                  <span>Əsas qiymət:</span>
                  <span>{basePrice.toFixed(2)} AZN</span>
                </div>
                {isFreeCourse && (
                  <div className="price-row discount" style={{ color: '#22c55e' }}>
                    <span>Endirim (100%):</span>
                    <span>-{basePrice.toFixed(2)} AZN</span>
                  </div>
                )}
                {discountApplied && !isFreeCourse && (
                  <div className="price-row discount">
                    <span>Endirim (20%):</span>
                    <span>-{(basePrice - finalPrice).toFixed(2)} AZN</span>
                  </div>
                )}
                <div className="price-row total">
                  <span>Yekun ödəniş:</span>
                  <span className="total-amount" style={{ 
                    color: isFreeCourse ? '#22c55e' : 'inherit',
                    fontSize: isFreeCourse ? '1.5rem' : 'inherit'
                  }}>
                    {isFreeCourse ? '🎉 PULSUZ' : `${finalPrice.toFixed(2)} AZN`}
                  </span>
                </div>
              </div>
              
              <div className="form-actions">
                <button type="button" onClick={() => setCurrentStep(2)} className="btn-secondary">
                  <ArrowLeft size={18} /> Geri
                </button>
                <div className="action-group">
                  <button type="button" onClick={skipPromo} className="btn-text">
                    Promo kod yoxdur
                  </button>
                  <button type="submit" className="btn-primary" disabled={isProcessing}>
                    {isProcessing ? <Loader2 className="animate-spin" size={18} /> : <ChevronRight size={18} />}
                    Təsdiqlə
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {currentStep === 4 && (
          <div className="payment-step">
            <h1>{isFreeCourse ? 'Pulsuz Təsdiq' : 'Ödəniş Təsdiqi'}</h1>
            
            {isFreeCourse ? (
              <>
                <p className="step-description">Promo kodu ilə pulsuz aktivləşdirmə</p>
                
                <div className="free-course-banner" style={{
                  background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                  color: 'white',
                  padding: '2rem',
                  borderRadius: '16px',
                  marginBottom: '1.5rem',
                  textAlign: 'center'
                }}>
                  <CheckCircle size={48} style={{ marginBottom: '1rem' }} />
                  <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem' }}>🎉 Təbriklər!</h3>
                  <p style={{ margin: 0, fontSize: '1.1rem' }}>
                    Promo kodu ilə bu kursu <strong>PULSUZ</strong> əldə edirsiniz.
                  </p>
                </div>

                <div className="payment-details-card">
                  <h3>Kurs Məlumatları</h3>
                  <div className="detail-row">
                    <span>Kurs:</span>
                    <strong>{COURSES[selectedCourse]?.name}</strong>
                  </div>
                  <div className="detail-row">
                    <span>Promo kod:</span>
                    <strong style={{ color: '#22c55e' }}>{promoCode.toUpperCase()}</strong>
                  </div>
                  <div className="detail-row">
                    <span>Qiymət:</span>
                    <strong style={{ color: '#22c55e', fontSize: '1.25rem' }}>PULSUZ</strong>
                  </div>
                </div>

                <div className="upload-actions" style={{ marginTop: '1.5rem' }}>
                  <button 
                    onClick={() => setCurrentStep(3)} 
                    className="btn-secondary"
                  >
                    <ArrowLeft size={18} /> Geri
                  </button>
                  
                  <button 
                    onClick={processPayment}
                    className="btn-verify"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem 1.5rem',
                      background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    <CheckCircle size={18} />
                    Pulsuz Aktivləşdir
                  </button>
                </div>
              </>
            ) : assignedCard && (
              <>
                <p className="step-description">Çekin şəklini yükləyin (OCR avtomatik yoxlayacaq)</p>
                
                <div className="check-instructions" style={{
                  background: '#eff6ff',
                  border: '2px solid #3b82f6',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  marginBottom: '1.5rem'
                }}>
                  <h4 style={{ 
                    margin: '0 0 1rem 0', 
                    color: '#1e40af',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '1.1rem'
                  }}>
                    <Info size={22} />
                    📸 Çek Necə Göndərilməlidir?
                  </h4>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: '0.75rem',
                      background: 'white',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '1px solid #bfdbfe'
                    }}>
                      <div style={{ 
                        background: '#3b82f6', 
                        color: 'white', 
                        width: '24px', 
                        height: '24px', 
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.875rem',
                        fontWeight: 'bold',
                        flexShrink: 0
                      }}>1</div>
                      <div>
                        <strong style={{ color: '#1e40af', display: 'block', marginBottom: '0.25rem' }}>
                          Bank tətbiqini açın
                        </strong>
                        <span style={{ color: '#4b5563', fontSize: '0.875rem' }}>
                          {assignedCard.bank} tətbiqində <strong>"Tarixçə"</strong> və ya <strong>"Əməliyyatlar"</strong> bölməsinə keçin
                        </span>
                      </div>
                    </div>

                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: '0.75rem',
                      background: 'white',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '1px solid #bfdbfe'
                    }}>
                      <div style={{ 
                        background: '#3b82f6', 
                        color: 'white', 
                        width: '24px', 
                        height: '24px', 
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.875rem',
                        fontWeight: 'bold',
                        flexShrink: 0
                      }}>2</div>
                      <div>
                        <strong style={{ color: '#1e40af', display: 'block', marginBottom: '0.25rem' }}>
                          Son ödənişi tapın
                        </strong>
                        <span style={{ color: '#4b5563', fontSize: '0.875rem' }}>
                          <strong>{finalPrice.toFixed(2)} AZN</strong> məbləğində son ödənişinizi açın. 
                          Çekdə <strong>məbləğ ({finalPrice.toFixed(2)} AZN)</strong>, 
                          <strong> tarix</strong>, 
                          <strong> saat</strong> və 
                          <strong> kart son 4 rəqəm (****{assignedCard.last4})</strong> 
                          dəqiq görünməlidir
                        </span>
                      </div>
                    </div>

                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: '0.75rem',
                      background: 'white',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '1px solid #bfdbfe'
                    }}>
                      <div style={{ 
                        background: '#3b82f6', 
                        color: 'white', 
                        width: '24px', 
                        height: '24px', 
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.875rem',
                        fontWeight: 'bold',
                        flexShrink: 0
                      }}>3</div>
                      <div>
                        <strong style={{ color: '#1e40af', display: 'block', marginBottom: '0.25rem' }}>
                          Ekran şəkli (screenshot) çəkin
                        </strong>
                        <span style={{ color: '#4b5563', fontSize: '0.875rem' }}>
                          Bütün məlumatların aydın olduğundan əmin olun. 
                          <strong style={{ color: '#dc2626' }}> Blurlu, qaranlıq və ya kəsilmiş şəkillər qəbul olunmur!</strong>
                        </span>
                      </div>
                    </div>

                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: '0.75rem',
                      background: '#fef3c7',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '1px solid #f59e0b'
                    }}>
                      <AlertTriangle size={20} color="#f59e0b" style={{ flexShrink: 0, marginTop: '2px' }} />
                      <span style={{ color: '#92400e', fontSize: '0.875rem' }}>
                        <strong>💡 Məsləhət:</strong> Bank tətbiqlərinin "Tarixçə" bölməsində çeklər tam və detallı göstərilir. 
                        Ən yaxşı nəticə üçün oradan screenshot çəkin.
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="payment-instruction-banner" style={{
                  background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                  color: 'white',
                  padding: '1.5rem',
                  borderRadius: '12px',
                  marginBottom: '1.5rem',
                  textAlign: 'center',
                  boxShadow: '0 4px 15px rgba(220, 38, 38, 0.3)'
                }}>
                  <AlertTriangle size={32} style={{ marginBottom: '0.5rem' }} />
                  <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem' }}>⚠️ Diqqət! Ödəniş Göndəriləcək Kart</h3>
                  <p style={{ margin: 0, fontSize: '1rem', opacity: 0.95 }}>
                    Pul yalnız aşağıda göstərilən kart nömrəsinə göndərilməlidir!
                  </p>
                </div>
                
                <div className="assigned-card-container" style={{ 
                  background: assignedCard.bg,
                  border: `3px solid ${assignedCard.color}`,
                  borderRadius: '16px',
                  padding: '2rem',
                  marginBottom: '1.5rem',
                  color: 'white',
                  boxShadow: `0 10px 30px ${assignedCard.color}40`
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                    <div style={{ 
                      width: '60px', 
                      height: '60px', 
                      background: 'rgba(255,255,255,0.2)',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '2rem',
                      backdropFilter: 'blur(10px)'
                    }}>
                      {assignedCard.logo}
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.5rem' }}>{assignedCard.bank}</h3>
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', opacity: 0.9 }}>
                      Sizə təsadüfi təyin edilmişdir
                      </p>
                    </div>
                  </div>
                  
                  <div className="card-number-display" style={{
                    background: 'rgba(0,0,0,0.3)',
                    color: 'white',
                    padding: '1.5rem',
                    borderRadius: '12px',
                    fontFamily: 'monospace',
                    fontSize: '1.75rem',
                    letterSpacing: '4px',
                    textAlign: 'center',
                    marginBottom: '1.5rem',
                    border: '2px dashed rgba(255,255,255,0.3)',
                    textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }}>
                    {assignedCard.number}
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.75rem',
                    background: 'rgba(0,0,0,0.2)',
                    padding: '1rem',
                    borderRadius: '8px',
                    fontSize: '0.9375rem'
                  }}>
                    <Hash size={20} />
                    <span>Çekdə son 4 rəqəm <strong>({assignedCard.last4})</strong> görünməlidir</span>
                  </div>
                </div>

                <div className="payment-details-card">
                  <h3>Ödəniş Məlumatları</h3>
                  <div className="detail-row">
                    <span>Kurs:</span>
                    <strong>{COURSES[selectedCourse]?.name}</strong>
                  </div>
                  <div className="detail-row">
                    <span>Məbləğ:</span>
                    <strong className="highlight-price" style={{ 
                      color: '#22c55e',
                      fontSize: '1.25rem'
                    }}>
                      {finalPrice.toFixed(2)} AZN
                    </strong>
                  </div>
                  <div className="detail-row">
                    <span>Bank:</span>
                    <strong style={{ color: assignedCard.color }}>{assignedCard.bank}</strong>
                  </div>
                  <div className="detail-row" style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                    <span>Cari vaxt:</span>
                    <span>{currentTime.toLocaleString('az-AZ')}</span>
                  </div>
                </div>

                <div className="strict-requirements" style={{
                  background: '#fef3c7',
                  border: '1px solid #f59e0b',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1rem'
                }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#92400e', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Shield size={18} />
                    Təhlükəsizlik Tələbləri
                  </h4>
                  <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.875rem', color: '#78350f' }}>
                    <li>Məbləğ dəqiq <strong>{finalPrice.toFixed(2)} AZN</strong> olmalıdır (±5 qəpik tolerans)</li>
                    <li>Çek tarixi max <strong>24 saat</strong> əvvəl olmalıdır</li>
                    <li>Çekdə kart son 4 rəqəm <strong>({assignedCard.last4})</strong> dəqiq görünməlidir</li>
                    <li>Şəkil aydın, oxunaqlı və kəsilmiş olmamalıdır</li>
                    <li><strong style={{color: '#dc2626'}}>Bank tətbiqinin "Tarixçə" bölməsindən screenshot ən yaxşısıdır!</strong></li>
                  </ul>
                </div>

                <div 
                  className={`upload-area ${filePreview ? 'has-preview' : ''}`}
                  onDrop={handleDrop}
                  onDragOver={(e) => e.preventDefault()}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    hidden
                  />
                  
                  {filePreview ? (
                    <div className="preview-container">
                      <img src={filePreview} alt="Payment receipt" />
                      <button 
                        type="button" 
                        className="remove-preview"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile();
                        }}
                      >
                        <X size={20} />
                      </button>
                    </div>
                  ) : (
                    <div className="upload-placeholder">
                      <Upload size={48} color="#6366f1" />
                      <p>Çekin şəklini bura sürükləyin və ya klikləyin</p>
                      <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        JPG, PNG, WEBP (max 10MB) • Bank tətbiqindən screenshot
                      </span>
                    </div>
                  )}
                </div>

                {isProcessing && (
                  <div className="ocr-progress" style={{ marginTop: '1rem' }}>
                    <div className="progress-bar" style={{ height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                      <div className="progress-fill" style={{ 
                        width: `${ocrProgress}%`, 
                        height: '100%', 
                        background: 'linear-gradient(90deg, #6366f1, #8b5cf6)',
                        transition: 'width 0.3s ease'
                      }}></div>
                    </div>
                    <span style={{ display: 'block', marginTop: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                      {ocrProgress < 100 ? `Şəkil işlənir: ${ocrProgress}%` : 'Mətn tanınır...'}
                    </span>
                  </div>
                )}

                {/* Validasiya detalları admin üçün gizli saxlanılır */}

                <div className="upload-actions" style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <button 
                    onClick={() => setCurrentStep(3)} 
                    className="btn-secondary"
                    disabled={isProcessing}
                  >
                    <ArrowLeft size={18} /> Geri
                  </button>
                  
                  <button 
                    onClick={processPayment}
                    className="btn-verify"
                    disabled={!uploadedFile || isProcessing}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem 1.5rem',
                      background: uploadedFile ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : '#9ca3af',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '500',
                      cursor: uploadedFile ? 'pointer' : 'not-allowed',
                      flex: '1',
                      justifyContent: 'center'
                    }}
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        Yoxlanılır...
                      </>
                    ) : (
                      <>
                        <Shield size={18} />
                        Ödənişi Təsdiqlə
                      </>
                    )}
                  </button>
                </div>

                <div className="ocr-info" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#6b7280', fontSize: '0.875rem', marginTop: '1rem' }}>
                  <FileText size={14} />
                  <p>Sistem avtomatik olaraq məbləğ, tarix, saat, kart nömrəsi və uğur statusunu yoxlayır</p>
                </div>
              </>
            )}
          </div>
        )}

        {currentStep === 5 && (
          <div className="payment-step success-step">
            <div className="success-animation">
              <CheckCircle size={64} color="#22c55e" />
            </div>
            
            <h1>{isFreeCourse ? '🎉 Pulsuz Aktivləşdirmə Uğurlu!' : 'Ödəniş Uğurlu!'}</h1>
            <p className="success-message">
              {isFreeCourse 
                ? 'Promo kod ilə pulsuz kurs əldə etdiniz!' 
                : 'Aktivləşdirmə kodunuz hazırdır'}
            </p>
            
            {isFreeCourse && (
              <div style={{
                background: '#dcfce7',
                border: '1px solid #22c55e',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <CheckCircle size={20} color="#22c55e" />
                <span style={{ fontSize: '0.875rem', color: '#166534' }}>
                  <strong>{promoCode.toUpperCase()}</strong> promo kodu uğurla tətbiq edildi
                </span>
              </div>
            )}
            
            {/* WhatsApp bildirişi silindi - admin üçün gizli */}
            
            {showFraudWarning && (
              <div className="fraud-warning" style={{
                background: '#fef3c7',
                border: '2px solid #f59e0b',
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                maxWidth: '400px'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                  <AlertTriangle size={32} color="#f59e0b" />
                  <div className="fraud-warning-content">
                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#92400e' }}>⚠️ Vacib Xəbərdarlıq</h4>
                    <p style={{ margin: 0, fontSize: '0.875rem', color: '#78350f', lineHeight: '1.5' }}>
                      Əgər sizin ödəniş ilə bağlı hər hansısa saxtakarlıq aşkarlanarsa, 
                      hesabınız dərhal bloklanacaq. Bütün əməliyyatlar loglarda qeydə alınır.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="activation-code-container">
              <div className="code-box" style={{
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                padding: '1.5rem 2rem',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                boxShadow: '0 10px 25px rgba(99, 102, 241, 0.3)'
              }}>
                <code style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: 'bold',
                  color: 'white',
                  letterSpacing: '2px'
                }}>
                  {activationCode}
                </code>
                <button 
                  onClick={copyToClipboard}
                  className={`copy-btn ${copied ? 'copied' : ''}`}
                  title="Kopyala"
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '0.5rem',
                    cursor: 'pointer',
                    color: 'white'
                  }}
                >
                  {copied ? <Check size={20} /> : <Copy size={20} />}
                </button>
              </div>
              <p className="code-instruction" style={{ color: '#6b7280', marginTop: '1rem' }}>
                Bu kodu kurs səhifəsində "Kodu daxil et" bölməsinə yazın
              </p>
            </div>

            {/* Validasiya faizi silindi - admin üçün gizli */}

            <div className="success-actions-final" style={{ marginTop: '2rem' }}>
              <Link to={`/${selectedCourse}`} className="btn-primary" style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                background: '#6366f1',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '500'
              }}>
                Kursa Keç <ChevronRight size={18} />
              </Link>
              <button onClick={resetForm} className="btn-secondary" style={{
                marginLeft: '1rem',
                padding: '0.75rem 1.5rem'
              }}>
                Yeni Ödəniş
              </button>
            </div>
          </div>
        )}
      </main>

      <footer className="payment-footer-simple">
        <p>© 2024 Lumina • Təhlükəsiz ödəniş sistemi</p>
        <div className="security-badges" style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.875rem', color: '#6b7280' }}>
            <Lock size={14} /> SSL
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.875rem', color: '#6b7280' }}>
            <Shield size={14} /> OCR Sistem
          </span>
        </div>
      </footer>
    </div>
  );
};

export default Payment;